import { memo, useEffect } from "react";
import Box from "@mui/material/Box";
import { Resource, ResourceProps } from "ui/widgets/Resource";
import { TableGridMode } from "consts/table";
import { Filters } from "consts/merchants";
import { Option } from "util/option";
import {
    selectIsMerchantsLoading,
    selectMerchantsSlice
} from "features/merchants/selectors";
import useMerchants from "./useMerchants";
import MerchantToolbar from "./MerchantToolbar";
import MerchantCreateEdit from "./MerchantCreateEdit";
import usePaymentMethodActions from "features/paymentMethods/useActions";
import { selectPaymentMethodsData } from "features/paymentMethods/selectors";
import { useTypedSelector } from "hooks";
import ToolbarFilters, {
    useToolbarFiltersContext
} from "../shared/ToolbarFilters";

type Props = Pick<
    ResourceProps,
    | 'LayoutProps'
    | 'filterTypes'
    | 'FiltersProps'
    | 'columns'
>;

const Merchants = ({
    LayoutProps,
    filterTypes,
    FiltersProps,
    columns
}: Props) => {
    const { filtersRef } = useToolbarFiltersContext();
    const { getAllPaymentMethods } = usePaymentMethodActions();

    // No-op once the full collection is loaded (dashboard bootstrap)
    useEffect(() => {
        getAllPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const pspOptions = Array.from(new Set(
        useTypedSelector(selectPaymentMethodsData()).map(({ PSP }) => PSP).filter(Boolean)
    )).sort().map(psp => Option.make(psp));

    return (
        <Box
            sx={{
                position: 'relative'
            }}
        >
            <Resource
                name='Merchant'
                useResource={useMerchants}
                mode={TableGridMode.Merchant}
                selectIsTableUninitialized={() => true}
                selectIsTableLoading={selectIsMerchantsLoading()}
                selectTableDataSlice={selectMerchantsSlice()}
                Toolbar={MerchantToolbar}
                Detail={MerchantCreateEdit}
                LayoutProps={LayoutProps}
                filterTypes={filterTypes}
                FiltersProps={{
                    ...FiltersProps,
                    filtersModelOverrides: {
                        ...FiltersProps?.filtersModelOverrides,
                        [Filters.enabledAssignablePaymentMethodPSP]: pspOptions
                    }
                }}
                columns={columns}
                filtersRef={filtersRef}
            />
        </Box>
    );
};

// The active status filter lives in the filters panel; the provider still supplies filtersRef
export default memo((props: Props) => (
    <ToolbarFilters
        toolbarFilters={new Map()}
    >
        <Merchants
            {...props}
        />
    </ToolbarFilters>
));
